import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Info, X, ExternalLink } from 'lucide-react';

export const SOPS = {
  montage: { url: '/sop/sop-montage.mp4', label: 'SOP Montage', title: 'SOP Montage', sub: 'Le process à suivre sur chaque vidéo. Regarde-la en entier avant ton premier montage.' },
  posteur: { url: '/sop/sop-posteur.mp4', label: 'SOP Posts', title: 'SOP · Comment gérer les posts', sub: 'Le process à suivre pour chaque vidéo à poster. Regarde-la en entier avant ton premier post.' },
};

export const SopVideoButton = ({ compact = false, variant = 'montage' }) => {
  const sop = SOPS[variant];
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={variant === 'montage' ? 'sop-video-btn' : 'sop-posteur-btn'}
        onClick={() => setOpen(true)}
        title={`${sop.title} — la vidéo explicative`}
        className="sop-btn"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: compact ? '0 11px 0 8px' : '7px 13px 7px 9px',
          height: compact ? 36 : 'auto',
          justifyContent: 'center',
          borderRadius: 999,
          background: 'linear-gradient(135deg, rgba(245,197,106,0.20) 0%, rgba(217,154,66,0.12) 100%)',
          border: '1px solid rgba(245,197,106,0.45)',
          color: '#F5C56A', cursor: 'pointer',
          fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
          boxShadow: '0 6px 18px -8px rgba(245,197,106,0.55)',
          transition: 'transform 0.14s var(--ease-out), box-shadow 0.14s var(--ease-out)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        <Info size={15} strokeWidth={2.6} />
        {compact ? (variant === 'montage' ? 'SOP' : 'SOP Posts') : sop.label}
      </button>
      {open && createPortal(<SopVideoModal sop={sop} variant={variant} onClose={() => setOpen(false)} />, document.body)}
    </>
  );
};

export const SopVideoModal = ({ sop, variant, onClose }) => (
  <div
    data-testid="sop-video-modal"
    onClick={onClose}
    style={{ position: 'fixed', inset: 0, zIndex: 220, background: 'rgba(6,12,26,0.82)', backdropFilter: 'blur(14px)', display: 'grid', placeItems: 'center', padding: 16 }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="scale-in"
      style={{ width: '100%', maxWidth: 920, maxHeight: '92vh', overflowY: 'auto', borderRadius: 22, padding: 18, background: 'linear-gradient(180deg, rgba(20,29,66,0.85) 0%, rgba(14,22,44,0.98) 100%)', border: '1px solid rgba(245,197,106,0.28)', boxShadow: '0 40px 90px -20px rgba(0,0,0,0.75)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <span style={{ width: 38, height: 38, borderRadius: 12, display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg, #F5C56A, #D99A42)', color: '#0B1330' }}><Info size={18} strokeWidth={2.8} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 17, color: '#F1F4F9', letterSpacing: '-0.01em' }}>{sop.title}</div>
          <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 12, color: 'rgba(200,215,240,0.55)' }}>{sop.sub}</div>
        </div>
        <a
          href={sop.url} target="_blank" rel="noopener noreferrer"
          data-testid="sop-video-open-external"
          title="Ouvrir dans un nouvel onglet"
          style={{ padding: 8, borderRadius: 10, color: 'rgba(200,215,240,0.72)', border: '1px solid rgba(62,123,250,0.18)', display: 'grid', placeItems: 'center' }}
        ><ExternalLink size={15} /></a>
        <button type="button" data-testid="sop-video-close" onClick={onClose} style={{ padding: 8, borderRadius: 10, background: 'transparent', border: '1px solid rgba(62,123,250,0.18)', color: 'rgba(200,215,240,0.72)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><X size={15} /></button>
      </div>
      <video
        data-testid="sop-video-player"
        src={sop.url}
        controls
        autoPlay
        playsInline
        preload="metadata"
        style={{ width: '100%', maxHeight: '58vh', borderRadius: 14, background: '#000', display: 'block' }}
      />
      {variant === 'montage' && <SopGuide />}
    </div>
  </div>
);

const SOP_STEPS = [
  ['Ouvre « Tâches »', "Ton Kanban du jour affiche toutes tes vidéos à monter (cartes vidéo avec le format). L'onglet Calendrier te montre les jours suivants."],
  ['Clique sur la carte vidéo', 'Tu as le lien des rushs (Drive), ton dossier de travail et les instructions de montage.'],
  ['La fiche complète est dans « Projets »', 'Projets → ton projet → clique sur la vidéo → bloc « Consignes du montage » : titre, format, client, inspi, lien Drive.'],
  ['Lance-toi', 'Passe la carte en « En cours » quand tu commences : le chrono démarre tout seul.'],
  ['Vidéo finie', 'Optionnel : colle ton lien de rendu dans « Livraison rapide ». Puis passe la vidéo en « Terminé » → elle part direct chez le posteur (« À poster »).'],
];

const SopGuide = () => (
  <div data-testid="sop-guide" style={{ marginTop: 16, padding: '14px 16px', borderRadius: 14, background: 'rgba(62,123,250,0.06)', border: '1px solid rgba(62,123,250,0.16)' }}>
    <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5, color: '#F1F4F9', marginBottom: 10 }}>Comment trouver et monter tes vidéos</div>
    {SOP_STEPS.map(([title, txt], i) => (
      <div key={title} data-testid={`sop-step-${i + 1}`} style={{ display: 'flex', gap: 12, padding: '7px 0' }}>
        <span style={{ width: 24, height: 24, borderRadius: 999, flexShrink: 0, display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg, #F5C56A, #D99A42)', color: '#0B1330', fontFamily: 'Sora', fontWeight: 800, fontSize: 12 }}>{i + 1}</span>
        <div>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: '#F1F4F9' }}>{title}</div>
          <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 12, color: 'rgba(200,215,240,0.62)', lineHeight: 1.5, marginTop: 2 }}>{txt}</div>
        </div>
      </div>
    ))}
  </div>
);
